import { Component, Inject, inject, OnInit } from '@angular/core';
import { AmplifyUser } from '@aws-amplify/ui';
import { ConfigService } from '../../../config.service';
import { MatDialog } from '@angular/material/dialog';
import { AWSLambda } from 'src/app/AWSLambda.service';
import { Router, ActivatedRoute } from '@angular/router';
import { Auth } from 'aws-amplify';
import { GlobalDataService } from '../../../services/global-data.service';
import { detectEnv } from 'src/app/services/Enviroment';
import { TranslateService } from '@ngx-translate/core';
import {AuthService} from "../../../services/auth.service";
import { BoRolesCheckService } from 'src/app/components/rolesCheck/bo-roles-check.service';
import { BOEsimDetailsModalComponent } from './esim-details-modal.component';

@Component({
  selector: 'app-backoffice-esim-list',
  templateUrl: './esimList.component.html',
  styles: [
    `
      .loading-mask {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;
      }
    `,
  ],
})
export class BOEsimListComponent implements OnInit {
  showSpinner = false;
  window = window;
  dialog = inject(MatDialog);
  lambda = inject(AWSLambda);
  rolesCheck = inject(BoRolesCheckService);
  protected currentEnv = detectEnv();
  user!: AmplifyUser;
  canAccess = false;
  currentUserId: string = '';

  esimList: any[] = [];
  loadingEsims: boolean = false;
  filteredCounts: { esimList: number } = { esimList: 0 };
  filterEsims: any[] = [];
  userData: any = null;
  loadingUserData: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private auth: AuthService,
  ) {}

  async ngOnInit() {
    this.canAccess = await this.rolesCheck.checkAccess();
    if (!this.canAccess) {
      return;
    }

    this.currentUserId = this.route.snapshot.paramMap.get('userId') || '';
    if (!this.currentUserId) {
      this.router.navigate(['/backoffice/users']);
      return;
    }

    await this.auth.refresh();
    this.user = await Auth.currentAuthenticatedUser();
    await this.getUserData();
    this.getEsimList();
  }

  async getUserData(): Promise<void> {
    this.loadingUserData = true;
    try {
      const data = await this.lambda.getLambdaFunction("lambda", "/get/userDetails", {
        queryStringParameters: {
          sub: this.currentUserId
        }
      });
      this.userData = data;
    } catch (error) {
      console.error('Error al obtener datos del usuario:', error);
      this.userData = null;
    } finally {
      this.loadingUserData = false;
    }
  }

  async getEsimList(): Promise<void> {
    if (this.loadingEsims) return;
    this.loadingEsims = true;

    try {
      const response = await this.lambda.getLambdaFunction('lambda', '/esim/getUserEsimList', {
        queryStringParameters: { owner: this.currentUserId }
      });

      if (response && Array.isArray(response)) {
        this.esimList = response.map(esim => this.transformEsimData(esim))
          .sort((a, b) => {
            const dateA = new Date(a.purchaseDate).getTime();
            const dateB = new Date(b.purchaseDate).getTime();
            return dateB - dateA;
          });
        
        console.log('esimList', this.esimList);

        const uniqueStatuses = Array.from(
          new Set(this.esimList.map((esim: { status: any; }) => esim.status))
        ).map(status => ({
          key: status,
          value: status
        }));

        this.filterEsims = [
          { key: 'country', type: 'text', label: 'País' },
          { key: 'status', type: 'select', label: 'Estado', options: uniqueStatuses },
        ];
      } else {
        this.esimList = [];
      }
    } catch (error) {
      console.error('Error al obtener eSIMs:', error);
      this.esimList = [];
    } finally {
      this.loadingEsims = false;
    }
  }

  private transformEsimData(esim: any): any {
    const usage = esim.usage_data || {};
    const pkgDetails = esim.pkg_details || {};
    const operatorInfo = pkgDetails.operator_info || {};
    const details = esim.details || {};
    const status = esim.esim_status;

    // Validity period from package details (in days)
    const validityDays = pkgDetails.day || esim.validity || null;

    return {
      iccid: esim.iccid,
      country: pkgDetails.country_title || esim.country || 'N/A',
      operator: esim.operator || operatorInfo.title || 'N/A',
      type: operatorInfo.type || 'N/A',
      status: status, // [ NOT_ACTIVE, ACTIVE, FINISHED, UNKNOWN, EXPIRED ]

      // Core date fields - always from database
      purchaseDate: esim.createdAt ? new Date(esim.createdAt).toISOString().split('T')[0] : null,
      activationDate: esim.activated_at ? new Date(esim.activated_at).toISOString().split('T')[0] : null,
      finishDate: esim.finished_at ? new Date(esim.finished_at).toISOString().split('T')[0] : null,
      expiredDate: esim.expired_at ? new Date(esim.expired_at).toISOString().split('T')[0] : null,
      recycledDate: esim.recycled_at ? new Date(esim.recycled_at).toISOString().split('T')[0] : null,

      // Calculated fields
      recyclingDeadlineDate: (status === 'NOT_ACTIVE' && esim.createdAt)
        ? this.calculateRecyclingDate(esim.createdAt)
        : null,
      expiryDate: this.calculateExpiryDate(esim, status, validityDays),

      // Usage data
      dataUsageDisplay: this.formatUsageDisplay(usage.total - usage.remaining, usage.total),
      dataUsed: usage.total - usage.remaining,
      dataLimit: usage.total,
      voiceUsageDisplay: this.formatUsageDisplay(usage.total_voice - usage.remaining_voice, usage.total_voice, 'voice'),
      voiceUsed: usage.total_voice - usage.remaining_voice,
      voiceLimit: usage.total_voice,
      textUsageDisplay: this.formatUsageDisplay(usage.total_text - usage.remaining_text, usage.total_text, 'text'),
      textUsed: usage.total_text - usage.remaining_text,
      textLimit: usage.total_text,

      // Store original data for reference
      originalData: esim
    };
  }

  private calculateRecyclingDate(createdAt: string): string {
    const created = new Date(createdAt);
    const recycling = new Date(created.getTime() + (90 * 24 * 60 * 60 * 1000));
    return recycling.toISOString().split('T')[0];
  }

  private calculateExpiryDate(esim: any, status: string, validityDays: number | null): string | null {
    // Only calculate for ACTIVE eSIMs
    if (status !== 'ACTIVE') {
      return null;
    }

    // If expired_at exists in DB, use it
    if (esim.expired_at) {
      return new Date(esim.expired_at).toISOString().split('T')[0];
    }

    // Calculate from activated_at + validity days
    if (esim.activated_at && validityDays) {
      const activated = new Date(esim.activated_at);
      const expiry = new Date(activated.getTime() + (validityDays * 24 * 60 * 60 * 1000));
      return expiry.toISOString().split('T')[0];
    }

    return null;
  }


  getPageTitle(): string {
    if (!this.userData) {
      return this.translate.instant('BACKOFFICE.USERS.ESIM_LIST.TITLE');
    }

    const dni = this.userData['custom:passport'] || this.userData['custom:dni'] || 'N/A';
    const fullName = this.userData.name || this.userData['custom:name'] || 'Usuario';
    const middleName = this.userData['middle_name'] || this.userData['custom:middle_name'] || '';
    const completeName = middleName ? `${fullName} ${middleName}` : fullName;

    return `Listado de eSIMs de ${completeName} - ${dni}`;
  }

  onEsimClick(esim: any): void {
    this.dialog.open(BOEsimDetailsModalComponent, {
      width: '600px',
      data: esim
    });
  }
  
  onFiltersChanged(filters: any) {
    console.log('Filters updated:', filters);
  }

  goBack(): void {
    this.router.navigate(['/backoffice/users', this.currentUserId]);
  }

  delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async refreshEsimList(): Promise<void> {
    this.esimList = [];
    await this.getEsimList();
  }

  private formatUsageDisplay(used: number, total: number, type: string = 'data'): string {
    const percentage = total > 0 ? Math.round((used / total) * 100) : 0;
    
    if (type === 'data') {
      const usedGB = used.toFixed(0);
      const totalGB = total.toFixed(0);
      return `${usedGB} / ${totalGB} (${percentage}%)`;
    } else {
      return `${used} / ${total} (${percentage}%)`;
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'ACTIVE':
        return 'text-green-600 bg-green-100';
      case 'NOT_ACTIVE':
        return 'text-gray-600 bg-gray-100';
      case 'FINISHED':
        return 'text-blue-600 bg-blue-100';
      case 'EXPIRED':
        return 'text-red-600 bg-red-100';
      case 'UNKNOWN':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  }
}
